import { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import { AuthState, clinicianSignUp, getUserAccess } from '../AuthState';
import { post } from '../../api/baseUrl';
import {
  Clinic,
  Tenancy,
  ClinicianSignUpData,
  SignUpResponse,
  SelectedClinics,
  SelectedTenancyCount,
  AuthStep,
} from '../types';
import { useAuthStep } from '../context/AuthStepContext';

export const useClinicianSignUp = () => {
  const { nextStep, skipTo, setIsSignInFlow, setHasAccount } = useAuthStep();
  // Core state
  const [clinicsState, setClinics] = useState<Clinic[]>([]);
  const [selectedTenancyCount, setSelectedTenancyCount] =
    useState<SelectedTenancyCount>({});
  const [selected, setSelected] = useState<SelectedClinics>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFetchingData, setIsFetchingData] = useState<boolean>(false);

  // Form state
  const [pin, setPin] = useState<string>('');
  const [confirmPin, setConfirmPin] = useState<string>('');

  // Refs for form inputs
  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);
  const referralCodeRef = useRef<HTMLInputElement>(null);

  /**
   * Extract unique tenancies from clinic data and initialize selection state
   */
  const getTenancies = (clinics: Clinic[]): Tenancy[] => {
    // Extract unique tenancies using Set for better performance
    const uniqueTenancies = new Map<string, Tenancy>();
    const initialTenancyCount: SelectedTenancyCount = {};
    const initialClinicSelection: SelectedClinics = {};

    // Process each clinic to extract unique tenancies
    clinics.forEach((clinic) => {
      const tenancyId = clinic.tenancy_id;

      // Add unique tenancy
      if (!uniqueTenancies.has(tenancyId)) {
        uniqueTenancies.set(tenancyId, {
          tenancy_id: tenancyId,
          tenancy_name: clinic.tenancy_name || `Tenancy ${tenancyId}`,
        });
        initialTenancyCount[tenancyId] = 0;
      }

      // Initialize clinic selection state
      initialClinicSelection[`${clinic.id}-input`] = false;
    });

    // Update state
    setClinics(clinics);
    setSelected(initialClinicSelection);
    setSelectedTenancyCount(initialTenancyCount);

    return Array.from(uniqueTenancies.values());
  };

  /**
   * Handle checkbox change for clinic selection
   */
  const handleChange = (
    ev: React.ChangeEvent<any>,
    tenancyId: string,
  ): void => {
    if (!ev.target) return;

    const newSelected = { ...selected };
    const newSelectedTenancyCount = { ...selectedTenancyCount };

    newSelected[ev.target.id] = ev.target.checked;

    if (ev.target.checked) {
      newSelectedTenancyCount[tenancyId] =
        (newSelectedTenancyCount[tenancyId] || 0) + 1;
    } else {
      newSelectedTenancyCount[tenancyId] = Math.max(
        (newSelectedTenancyCount[tenancyId] || 0) - 1,
        0,
      );
    }

    setSelected(newSelected);
    setSelectedTenancyCount(newSelectedTenancyCount);
  };

  /**
   * Fetch clinic data from API and process tenancies
   */
  const getData = async (): Promise<void> => {
    try {
      setIsFetchingData(true);
      const res = await post(`getAllClinics`, {});
      if (res?.data?.data) {
        getTenancies(res.data.data);
      } else {
        console.warn('No clinic data received from API');
      }
    } catch (err) {
      console.error('Failed to fetch clinic data:', err);
    } finally {
      setIsFetchingData(false);
    }
  };

  const validateConsent = (): boolean => {
    const consentCheckbox = document.getElementById(
      'consent',
    ) as HTMLInputElement;
    return consentCheckbox?.checked || false;
  };

  const validateName = (firstName: string, lastName: string): boolean => {
    return !!(firstName && lastName);
  };

  const validateReferralCode = (code: string): boolean => {
    return !!(code && code.length === 8 && /^\d+$/.test(code));
  };

  const validateClinics = (clinics: number[]): boolean => {
    return clinics.length > 0;
  };

  const validatePins = (pin: string, confirmPin: string): boolean => {
    return pin.length === 4 && pin === confirmPin;
  };

  const showValidationError = (message: string): void => {
    Swal.fire({
      icon: 'warning',
      text: message,
      showConfirmButton: true,
    });
  };

  /**
   * Get selected clinic IDs from the selection state
   */
  const getSelectedClinics = (): number[] => {
    return Object.entries(selected).reduce(
      (acc: number[], [selectedId, checked]) => {
        if (checked) {
          const clinicIdStr = selectedId.split('-')[1];
          const clinicId = parseInt(clinicIdStr, 10);
          if (!isNaN(clinicId)) {
            acc.push(clinicId);
          }
        }
        return acc;
      },
      [],
    );
  };

  const pinUpdated = (e: string): void => {
    setPin(e);
  };

  const confirmPinUpdated = (e: string): void => {
    setConfirmPin(e);
  };

  const validateFormData = (formData: ClinicianSignUpData): boolean => {
    // Validate form data
    if (!validateConsent()) {
      showValidationError('Please consent to the terms');
      return false;
    }

    if (!validateName(formData.firstName, formData.lastName)) {
      showValidationError('Please enter your name');
      return false;
    }

    if (!validateReferralCode(formData.referralCode)) {
      showValidationError('Please enter a valid referral code');
      return false;
    }

    if (!validateClinics(formData.clinics)) {
      showValidationError('Please select a clinic');
      return false;
    }

    if (formData.pin && !validatePins(formData.pin, confirmPin)) {
      showValidationError('Please ensure PIN and Confirm PIN match');
      return false;
    }
    return true;
  };

  /**
   * Handle form submission with improved validation
   */
  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();
    setIsLoading(true);
    try {
      // Collect form data
      const formData: ClinicianSignUpData = {
        firstName: firstNameRef.current?.value || '',
        lastName: lastNameRef.current?.value || '',
        referralCode: referralCodeRef.current?.value || '',
        email: AuthState.email,
        phone: AuthState.phone,
        pin: pin,
        clinics: getSelectedClinics(),
      };

      if (!validateFormData(formData)) {
        return;
      }

      // Submit form
      AuthState.email = formData.email;
      const res: SignUpResponse = await clinicianSignUp(
        formData.firstName,
        formData.lastName,
        formData.referralCode,
        formData.email,
        formData.pin,
        AuthState.phone,
        formData.clinics,
      );

      // Handle response
      if (res.message === 'Success') {
        nextStep();
      } else if (res.message === 'Already registered') {
        const userAccess = await getUserAccess();
        if (userAccess.userStatus === 1) {
          window.location.href = '/';
          return;
        } else {
          setIsLoading(false);
          setIsSignInFlow(false);
          setHasAccount(true);
          skipTo(AuthStep.COMPLETE);
        }
      } else {
        Swal.fire({
          icon: 'error',
          text: 'Invalid registration data. Please review.',
          showConfirmButton: true,
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
      Swal.fire({
        icon: 'error',
        text: 'An error occurred during registration. Please try again.',
        showConfirmButton: true,
      });
    }
  };

  useEffect(() => {
    getData();
  }, []);

  return {
    // Core state
    clinicsState,
    selectedTenancyCount,
    isLoading,
    selected,
    pin,
    confirmPin,
    isFetchingData,

    // Refs
    firstNameRef,
    lastNameRef,
    referralCodeRef,

    // Functions
    getTenancies,
    handleChange,
    getData,
    pinUpdated,
    confirmPinUpdated,
    handleSubmit,
  };
};
